import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, RefreshCw, Youtube, Instagram, Facebook, Save, CheckCircle2, Plus, Trash2, Edit2, X, Upload, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { formatNumber } from "@/components/AnimatedCounter";
import { ScrollToTop } from "@/components/shared";
import ImageUploader from "@/components/ImageUploader";
import FocalPointPicker from "@/components/FocalPointPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TikTokIcon = ({ size = 18 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.77 1.52V6.96a4.84 4.84 0 0 1-1.01-.27z"/></svg>);
const PLATFORMS = [
  { key: "youtubeMain", label: "YouTube (Main)", icon: Youtube, color: "#FF0000" },
  { key: "youtubeShorts", label: "YouTube Shorts", icon: Youtube, color: "#FF4444" },
  { key: "tiktok", label: "TikTok", icon: TikTokIcon, color: "#fff" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C" },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
];

function TabBtn({ active, onClick, children, testId }) {
  return (<button onClick={onClick} data-testid={testId}
    className={`px-4 py-2 text-xs md:text-sm font-medium transition-all whitespace-nowrap ${active ? "text-white border-b-2 border-[#E63946]" : "text-zinc-500 hover:text-zinc-300"}`}>{children}</button>);
}

const inputCls = "bg-[#050505] border-white/10 text-white placeholder:text-zinc-600 text-sm";

export default function Admin() {
  ScrollToTop();
  const [isAuth, setIsAuth] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [tab, setTab] = useState("followers");

  // State for all sections
  const [followers, setFollowers] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [longs, setLongs] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [activeG, setActiveG] = useState([]);
  const [pastG, setPastG] = useState([]);
  const [about, setAbout] = useState(null);
  const [contact, setContact] = useState(null);
  const [brands, setBrands] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [portHero, setPortHero] = useState(null);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [portCategories, setPortCategories] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [mediaPress, setMediaPress] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  // UI state
  const [ytSyncing, setYtSyncing] = useState(false);
  const [ytSyncMsg, setYtSyncMsg] = useState("");
  const [showForm, setShowForm] = useState(null); // "addLong","addShort","addPortfolio","addActiveG","addPastG"
  const [editing, setEditing] = useState(null); // {type, id, data}
  const [formData, setFormData] = useState({});
  const [msg, setMsg] = useState("");

  const headers = { "x-admin-password": password };

  const fetchAll = useCallback(async () => {
    try {
      const [f, v, p, g, a, c, b, m, ph, fp, pc, gl, mp, tm] = await Promise.all([
        axios.get(`${API}/followers`), axios.get(`${API}/videos`),
        axios.get(`${API}/content/portfolio`), axios.get(`${API}/content/giveaways`),
        axios.get(`${API}/content/about`), axios.get(`${API}/content/contact`),
        axios.get(`${API}/content/brands`),
        axios.get(`${API}/messages`, { headers: { "x-admin-password": password } }).catch(() => ({ data: { messages: [], unread: 0 } })),
        axios.get(`${API}/content/portfolio-hero`).catch(() => ({ data: {} })),
        axios.get(`${API}/content/featured-projects`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/content/portfolio-categories`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/content/gallery`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/content/media-press`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/content/testimonials`).catch(() => ({ data: { items: [] } })),
      ]);
      setFollowers(f.data); setEditVals({ youtubeMain: f.data.youtubeMain, youtubeShorts: f.data.youtubeShorts, tiktok: f.data.tiktok, instagram: f.data.instagram, facebook: f.data.facebook });
      setLongs(v.data.longs || []); setShorts(v.data.shorts || []);
      setPortfolio(p.data.items || []);
      setActiveG(g.data.active || []); setPastG(g.data.past || []);
      setAbout(a.data); setContact(c.data); setBrands(b.data.items || []);
      setMessages(m.data.messages || []); setUnreadCount(m.data.unread || 0);
      setPortHero(ph.data); setFeaturedProjects(fp.data.items || []);
      setPortCategories(pc.data.items || []); setGalleryItems(gl.data.items || []);
      setMediaPress(mp.data.items || []); setTestimonials(tm.data.items || []);
    } catch (e) { console.error(e); }
  }, [password]);

  useEffect(() => { if (isAuth) fetchAll(); }, [isAuth, fetchAll]);

  const handleLogin = async (e) => {
    e.preventDefault(); setAuthError("");
    try { await axios.put(`${API}/followers`, {}, { headers: { "x-admin-password": password } }); setIsAuth(true); }
    catch (err) { if (err.response?.status === 401) setAuthError("Invalid password"); else if (err.response?.status === 400) setIsAuth(true); else setAuthError("Connection error"); }
  };

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };

  // Generic handlers
  const handleSave = async (url, data, method = "put") => {
    try { await axios[method](url, data, { headers }); await fetchAll(); flash("Saved!"); return true; }
    catch (e) { console.error(e); return false; }
  };

  const handleDelete = async (url) => {
    try { await axios.delete(url, { headers }); await fetchAll(); flash("Deleted"); } catch (e) { console.error(e); }
  };

  // Login
  if (!isAuth) {
    return (
      <main data-testid="admin-page" className="pt-24 md:pt-28 pb-24 bg-[#050505] min-h-screen">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-8"><div className="w-16 h-16 mx-auto mb-4 border border-white/10 flex items-center justify-center bg-[#0F0F11]"><Lock size={24} className="text-[#E63946]" /></div>
            <h1 className="font-heading text-2xl font-bold text-white">Admin Panel</h1></div>
          <form onSubmit={handleLogin} className="p-6 bg-[#0F0F11] border border-white/5 space-y-4">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" data-testid="admin-password-input" className={inputCls} />
            {authError && <p className="text-xs text-red-400">{authError}</p>}
            <Button type="submit" data-testid="admin-login-btn" className="w-full bg-[#E63946] text-white hover:bg-[#F25C69]">Access Dashboard</Button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="pt-24 md:pt-28 pb-24 bg-[#050505] min-h-screen">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl font-bold text-white">Admin Dashboard</h1>
          {msg && <span className="text-sm text-green-400 animate-fade-in">{msg}</span>}
        </div>

        {/* Tabs */}
        <div className="flex gap-0.5 border-b border-white/10 mb-8 overflow-x-auto">
          {[["followers","Followers"],["longs","Videos"],["shorts","Shorts"],["featuredP","Featured"],["portCats","Categories"],["gallery","Gallery"],["mediaPress","Media"],["testimonials","Reviews"],["brands","Brands"],["messages",`Messages${unreadCount > 0 ? ` (${unreadCount})` : ""}`],["portHero","Hero"],["about","About"],["contact","Contact"]].map(([k,l]) => (
            <TabBtn key={k} active={tab === k} onClick={() => { setTab(k); setShowForm(null); setEditing(null); }} testId={`admin-tab-${k}`}>{l}</TabBtn>
          ))}
        </div>

        {/* ═══ FOLLOWERS ═══ */}
        {tab === "followers" && (
          <div>
            <div className="space-y-3">
              {PLATFORMS.map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="flex items-center gap-4 p-4 bg-[#0F0F11] border border-white/5">
                  <span style={{ color }}><Icon size={20} /></span>
                  <div className="flex-1"><span className="text-sm font-medium text-white">{label}</span><span className="text-xs text-zinc-600 font-mono ml-2">({formatNumber(followers?.[key] || 0)})</span></div>
                  <Input type="number" value={editVals[key] ?? ""} onChange={(e) => setEditVals({ ...editVals, [key]: parseInt(e.target.value) || 0 })} data-testid={`admin-input-${key}`} className={`w-32 text-right font-mono ${inputCls}`} />
                </div>
              ))}
            </div>
            <Button onClick={async () => { setSaving(true); await handleSave(`${API}/followers`, editVals); setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000); }} disabled={saving} data-testid="admin-save-btn" className="mt-6 bg-[#E63946] text-white hover:bg-[#F25C69] px-8">
              {saved ? <><CheckCircle2 size={16} className="mr-2" />Saved!</> : <><Save size={16} className="mr-2" />Save</>}
            </Button>
          </div>
        )}

        {/* ═══ LONG VIDEOS ═══ */}
        {tab === "longs" && <CrudList items={longs} type="longs" apiBase={`${API}/videos/longs`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          fields={[{k:"title",l:"Title",req:true},{k:"embedUrl",l:"YouTube Embed URL",req:true},{k:"thumbnail",l:"Thumbnail",img:true,aspect:"aspect-video"},{k:"views",l:"Views"},{k:"duration",l:"Duration"},{k:"category",l:"Category"}]}
          renderItem={(v) => (<><img src={v.thumbnail} alt="" className="w-20 h-12 object-cover rounded bg-zinc-800 shrink-0" loading="lazy" /><div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{v.title}</p><p className="text-[10px] text-zinc-600 font-mono">{v.views} {v.duration && `· ${v.duration}`}</p></div></>)}
          extraActions={<Button onClick={async () => { setYtSyncing(true); try { const r = await axios.post(`${API}/videos/sync-youtube`, {}, { headers }); setYtSyncMsg(r.data.message); await fetchAll(); } catch(e) { setYtSyncMsg("Failed"); } setYtSyncing(false); }} disabled={ytSyncing} variant="outline" data-testid="admin-sync-yt-videos" className="border-white/10 text-zinc-400 hover:text-white">
            <RefreshCw size={14} className={`mr-2 ${ytSyncing ? "animate-spin" : ""}`} />Sync YouTube</Button>} />}

        {/* ═══ SHORTS ═══ */}
        {tab === "shorts" && <CrudList items={shorts} type="shorts" apiBase={`${API}/videos/shorts`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          fields={[{k:"title",l:"Title",req:true},{k:"reelUrl",l:"Instagram Reel URL",req:true},{k:"views",l:"Views"}]}
          renderItem={(v) => (<><div className="w-10 h-14 rounded bg-zinc-800 flex items-center justify-center shrink-0"><Instagram size={14} className="text-pink-400" /></div><div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{v.title}</p><p className="text-[10px] text-zinc-600 font-mono truncate">{v.reelUrl}</p></div><span className="text-xs text-zinc-500 shrink-0">{v.views}</span></>)} />}

        {/* ═══ FEATURED PROJECTS ═══ */}
        {tab === "featuredP" && <CrudList items={featuredProjects} type="featuredP" apiBase={`${API}/content/featured-projects`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          label="Featured Work section on Portfolio page"
          fields={[{k:"title",l:"Project Title",req:true},{k:"category",l:"Category"},{k:"description",l:"Description",multi:true},{k:"videoUrl",l:"Video URL (IG reel or YT)"},{k:"embedUrl",l:"Embed URL"},{k:"thumbnail",l:"Thumbnail",img:true,aspect:"aspect-[9/16]"}]}
          renderItem={(v) => (<><div className="w-10 h-14 rounded bg-zinc-800 shrink-0 overflow-hidden">{v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />}</div><div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{v.title}</p><p className="text-[10px] text-zinc-600 font-mono truncate">{v.category}</p></div></>)} />}

        {/* ═══ PORTFOLIO CATEGORIES ═══ */}
        {tab === "portCats" && <CrudList items={portCategories} type="portCats" apiBase={`${API}/content/portfolio-categories`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          label="Category filter tabs on Portfolio page — Logo shown when category is selected"
          fields={[{k:"name",l:"Category Name",req:true},{k:"description",l:"Description"},{k:"logo",l:"Category Logo / Icon (PNG, square or 2:1)",img:true,aspect:"aspect-square"}]}
          renderItem={(v) => (<>{v.logo ? <img src={v.logo} alt={v.name} className="w-10 h-10 object-contain rounded bg-zinc-800 shrink-0 p-1" loading="lazy" /> : <div className="w-10 h-10 rounded bg-zinc-800 shrink-0" />}<div className="flex-1 min-w-0"><p className="text-sm text-white">{v.name}</p><p className="text-[10px] text-zinc-600 truncate">{v.description}</p></div></>)} />}

        {/* ═══ GALLERY ═══ */}
        {tab === "gallery" && <CrudList items={galleryItems} type="gallery" apiBase={`${API}/content/gallery`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          label="Project gallery grid on Portfolio page — Add extra videos to a project as one URL per line"
          fields={[{k:"title",l:"Title",req:true},{k:"category",l:"Category",req:true,options:portCategories.map(c=>c.name)},{k:"year",l:"Year"},{k:"description",l:"Short Description (shown on card)",multi:true},{k:"fullDescription",l:"Full Description (shown in modal)",multi:true},{k:"embedUrl",l:"Primary Embed/Video URL"},{k:"videos",l:"Additional Video URLs (one per line — optional)",multi:true},{k:"thumbnail",l:"Thumbnail",img:true,aspect:"aspect-video"}]}
          renderItem={(v) => (<>{v.thumbnail ? <img src={v.thumbnail} alt="" className="w-16 h-10 object-cover rounded bg-zinc-800 shrink-0" loading="lazy" /> : <div className="w-16 h-10 rounded bg-zinc-800 shrink-0" />}<div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{v.title}</p><p className="text-[10px] text-zinc-600 font-mono">{v.category} {v.year && `· ${v.year}`}{v.videos && ` · +${v.videos.split('\n').filter(Boolean).length} videos`}</p></div></>)} />}

        {/* ═══ MEDIA & PRESS ═══ */}
        {tab === "mediaPress" && <CrudList items={mediaPress} type="mediaPress" apiBase={`${API}/content/media-press`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          label="Featured In section on Portfolio page"
          fields={[{k:"title",l:"Title",req:true},{k:"category",l:"Category (Television/Press/Podcast/Guest)"},{k:"date",l:"Date"},{k:"embedUrl",l:"Embed URL"},{k:"link",l:"External Link"},{k:"thumbnail",l:"Thumbnail",img:true,aspect:"aspect-video"}]}
          renderItem={(v) => (<><div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{v.title}</p><p className="text-[10px] text-zinc-600 font-mono">{v.category} · {v.date}</p></div></>)} />}

        {/* ═══ TESTIMONIALS ═══ */}
        {tab === "testimonials" && <CrudList items={testimonials} type="testimonials" apiBase={`${API}/content/testimonials`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          label="Testimonials slider on Portfolio page"
          fields={[{k:"name",l:"Name",req:true},{k:"company",l:"Company"},{k:"position",l:"Position"},{k:"quote",l:"Quote",req:true,multi:true},{k:"avatar",l:"Avatar",img:true,aspect:"aspect-square"}]}
          renderItem={(v) => (<><div className="flex-1 min-w-0"><p className="text-sm text-white truncate">"{v.quote?.substring(0,50)}..."</p><p className="text-[10px] text-zinc-600">{v.name} — {v.position} at {v.company}</p></div></>)} />}

        {/* ═══ BRANDS ═══ */}
        {tab === "brands" && <CrudList items={brands} type="brands" apiBase={`${API}/content/brands`} headers={headers} fetchAll={fetchAll} flash={flash} password={password}
          label={`${brands.length} brand logos — Recommended: PNG, 400x200px (2:1), transparent bg`}
          fields={[{k:"name",l:"Brand Name",req:true},{k:"logo",l:"Brand Logo",img:true,aspect:"aspect-[2/1]"},{k:"link",l:"Website / Link URL"}]}
          renderItem={(v) => (<>{v.logo ? <img src={v.logo} alt={v.name} className="h-10 w-auto object-contain shrink-0 bg-zinc-800 rounded px-2 py-1" loading="lazy" /> : <div className="w-12 h-10 rounded bg-zinc-800 shrink-0" />}<div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{v.name}</p>{v.link && <p className="text-[10px] text-zinc-600 font-mono truncate">{v.link}</p>}</div></>)} />}

        {/* ═══ PORTFOLIO HERO ═══ */}
        {tab === "portHero" && portHero && <PortfolioHeroEditor data={portHero} headers={headers} fetchAll={fetchAll} flash={flash} />}

        {/* ═══ MESSAGES ═══ */}
        {tab === "messages" && <MessagesPanel messages={messages} headers={headers} fetchAll={fetchAll} flash={flash} />}

        {/* ═══ ABOUT ═══ */}
        {tab === "about" && about && <AboutEditor data={about} headers={headers} fetchAll={fetchAll} flash={flash} password={password} />}

        {/* ═══ CONTACT ═══ */}
        {tab === "contact" && contact && <ContactEditor data={contact} headers={headers} fetchAll={fetchAll} flash={flash} />}
      </div>
    </main>
  );
}

// ── Generic CRUD List Component ────────────────────────
function CrudList({ items, type, apiBase, headers, fetchAll, flash, fields, renderItem, extraActions, label, password }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({});

  const resetForm = () => { const d = {}; fields.forEach(f => d[f.k] = ""); return d; };

  const startEdit = (item) => {
    setEditId(item.id);
    const d = {};
    fields.forEach(f => d[f.k] = item[f.k] || "");
    setFormData(d);
  };

  const saveNew = async () => {
    try { await axios.post(apiBase, formData, { headers }); setShowAdd(false); setFormData(resetForm()); await fetchAll(); flash("Added!"); }
    catch (e) { console.error(e); }
  };

  const saveEdit = async (id) => {
    try { await axios.put(`${apiBase}/${id}`, formData, { headers }); setEditId(null); await fetchAll(); flash("Updated!"); }
    catch (e) { console.error(e); }
  };

  const del = async (id) => {
    try { await axios.delete(`${apiBase}/${id}`, { headers }); await fetchAll(); flash("Deleted"); }
    catch (e) { console.error(e); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-zinc-400">{label || `${items.length} items`}</span>
        <div className="flex gap-3">
          {extraActions}
          <Button onClick={() => { setShowAdd(true); setFormData(resetForm()); }} className="bg-[#E63946] text-white hover:bg-[#F25C69]" data-testid={`admin-add-${type}`}>
            <Plus size={14} className="mr-2" />Add
          </Button>
        </div>
      </div>

      {showAdd && (
        <div className="mb-4 p-4 bg-[#0F0F11] border border-[#E63946]/20 space-y-3">
          <div className="flex items-center justify-between"><span className="text-sm font-semibold text-white">Add New</span><button onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-white"><X size={16} /></button></div>
          <div className="grid grid-cols-2 gap-2">
            {fields.map(f => f.img ? (
              <div key={f.k} className="col-span-2">
                <ImageUploader currentUrl={formData[f.k] || ""} onUpload={(url) => setFormData({ ...formData, [f.k]: url })} password={password} label={f.l} aspectClass={f.aspect || "aspect-video"} />
              </div>
            ) : f.multi ? (
              <Textarea key={f.k} placeholder={f.l} value={formData[f.k] || ""} onChange={(e) => setFormData({ ...formData, [f.k]: e.target.value })} className={`col-span-2 ${inputCls}`} rows={3} />
            ) : f.options ? (
              <select key={f.k} value={formData[f.k] || ""} onChange={(e) => setFormData({ ...formData, [f.k]: e.target.value })} className={`${inputCls} h-9 px-3 rounded-md border`} data-testid={`select-${f.k}`}>
                <option value="">{f.l}{f.req ? " *" : ""}</option>
                {f.options.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
              </select>
            ) : (
              <Input key={f.k} placeholder={`${f.l}${f.req ? " *" : ""}`} value={formData[f.k] || ""} onChange={(e) => setFormData({ ...formData, [f.k]: e.target.value })} className={inputCls} />
            ))}
          </div>
          <Button onClick={saveNew} className="bg-[#E63946] text-white hover:bg-[#F25C69]"><Save size={14} className="mr-2" />Save</Button>
        </div>
      )}

      <div className="space-y-2">
        {items.map(item => (
          <div key={item.id} className="p-3 bg-[#0F0F11] border border-white/5 hover:border-white/10 transition-colors">
            {editId === item.id ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {fields.map(f => f.img ? (
                    <div key={f.k} className="col-span-2">
                      <ImageUploader currentUrl={formData[f.k] || ""} onUpload={(url) => setFormData({ ...formData, [f.k]: url })} password={password} label={f.l} aspectClass={f.aspect || "aspect-video"} />
                    </div>
                  ) : f.multi ? (
                    <Textarea key={f.k} placeholder={f.l} value={formData[f.k] || ""} onChange={(e) => setFormData({ ...formData, [f.k]: e.target.value })} className={`col-span-2 ${inputCls}`} rows={2} />
                  ) : f.options ? (
                    <select key={f.k} value={formData[f.k] || ""} onChange={(e) => setFormData({ ...formData, [f.k]: e.target.value })} className={`${inputCls} h-9 px-3 rounded-md border`}>
                      <option value="">{f.l}</option>
                      {f.options.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                    </select>
                  ) : (
                    <Input key={f.k} placeholder={f.l} value={formData[f.k] || ""} onChange={(e) => setFormData({ ...formData, [f.k]: e.target.value })} className={inputCls} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => saveEdit(item.id)} className="bg-[#E63946] text-white hover:bg-[#F25C69] text-xs px-4 h-8"><CheckCircle2 size={12} className="mr-1" />Save</Button>
                  <Button onClick={() => setEditId(null)} variant="outline" className="border-white/10 text-zinc-400 text-xs px-4 h-8">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {renderItem(item)}
                <button onClick={() => startEdit(item)} className="p-2 text-zinc-600 hover:text-white transition-colors shrink-0"><Edit2 size={14} /></button>
                <button onClick={() => del(item.id)} className="p-2 text-zinc-600 hover:text-red-400 transition-colors shrink-0"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── About Editor ───────────────────────────────────────
function AboutEditor({ data, headers, fetchAll, flash, password }) {
  const [bio, setBio] = useState(data.bio || "");
  const [style, setStyle] = useState(data.style || "");
  const [portrait, setPortrait] = useState(data.portrait || "");
  const [heroImage, setHeroImage] = useState(data.heroImage || "");
  const [heroMobileFocus, setHeroMobileFocus] = useState(data.heroMobileFocus || "50% 50%");
  const [heroDesktopFocus, setHeroDesktopFocus] = useState(data.heroDesktopFocus || "50% 50%");
  const [tagline, setTagline] = useState(data.tagline || "");
  const [heroDescription, setHeroDescription] = useState(data.heroDescription || "");
  const [footerText, setFooterText] = useState(data.footerText || "");
  const [journey, setJourney] = useState(JSON.stringify(data.journey || [], null, 2));

  const save = async () => {
    let parsedJourney;
    try { parsedJourney = JSON.parse(journey); } catch { flash("Invalid journey JSON"); return; }
    try {
      await axios.put(`${API}/content/about`, { bio, style, portrait, heroImage, heroMobileFocus, heroDesktopFocus, tagline, heroDescription, footerText, journey: parsedJourney }, { headers });
      await fetchAll(); flash("About updated!");
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6">
      {/* Hero Tagline & Description */}
      <div className="p-5 bg-[#0F0F11] border border-white/5 rounded-lg space-y-4">
        <h3 className="font-heading text-sm font-semibold text-white">Homepage Hero Text</h3>
        <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Tagline</label>
          <Input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Tech Content Creator | 500K+ Audience" className={inputCls} /></div>
        <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Hero Description</label>
          <Textarea value={heroDescription} onChange={(e) => setHeroDescription(e.target.value)} rows={2} placeholder="A filmmaker and director..." className={inputCls} /></div>
        <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Footer Text</label>
          <Textarea value={footerText} onChange={(e) => setFooterText(e.target.value)} rows={2} placeholder="Footer description text..." className={inputCls} /></div>
      </div>

      {/* Hero Image Upload */}
      <div className="p-5 bg-[#0F0F11] border border-white/5 rounded-lg space-y-5">
        <h3 className="font-heading text-sm font-semibold text-white flex items-center gap-2"><Upload size={14} className="text-[#E63946]" />Home Hero Image</h3>
        <ImageUploader currentUrl={heroImage} onUpload={setHeroImage} password={password} aspectClass="aspect-[21/9]" />

        {/* Mobile Focal Point */}
        <div className="pt-4 border-t border-white/5">
          <h4 className="font-heading text-xs font-semibold text-white mb-2 uppercase tracking-wider">Mobile Focal Point</h4>
          <FocalPointPicker
            src={heroImage}
            value={heroMobileFocus}
            onChange={setHeroMobileFocus}
            previewAspect="9/16"
            label="Click or drag anywhere on the image to set the part that must stay visible on mobile devices. The right-side preview shows exactly how the image will look on a phone."
          />
        </div>

        {/* Desktop Focal Point */}
        <div className="pt-4 border-t border-white/5">
          <h4 className="font-heading text-xs font-semibold text-white mb-2 uppercase tracking-wider">Desktop Focal Point</h4>
          <FocalPointPicker
            src={heroImage}
            value={heroDesktopFocus}
            onChange={setHeroDesktopFocus}
            previewAspect="21/9"
            label="Set the focal point for desktop and wide screens."
          />
        </div>
      </div>

      {/* Portrait Upload */}
      <div className="p-5 bg-[#0F0F11] border border-white/5 rounded-lg">
        <h3 className="font-heading text-sm font-semibold text-white mb-3 flex items-center gap-2"><Upload size={14} className="text-[#E63946]" />About Portrait</h3>
        <ImageUploader currentUrl={portrait} onUpload={setPortrait} password={password} aspectClass="aspect-[3/4]" />
      </div>

      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Bio</label>
        <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className={inputCls} /></div>
      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Style Description</label>
        <Textarea value={style} onChange={(e) => setStyle(e.target.value)} rows={3} className={inputCls} /></div>
      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Journey (JSON)</label>
        <Textarea value={journey} onChange={(e) => setJourney(e.target.value)} rows={8} className={`font-mono text-xs ${inputCls}`} /></div>
      <Button onClick={save} className="bg-[#E63946] text-white hover:bg-[#F25C69]"><Save size={14} className="mr-2" />Save About</Button>
    </div>
  );
}

// ── Contact Editor ─────────────────────────────────────
function ContactEditor({ data, headers, fetchAll, flash }) {
  const [email, setEmail] = useState(data.email || "");
  const [whatsapp, setWhatsapp] = useState(data.whatsapp || "");
  const [location, setLocation] = useState(data.location || "");
  const [socialLinks, setSocialLinks] = useState(JSON.stringify(data.socialLinks || [], null, 2));

  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(socialLinks); } catch { flash("Invalid social links JSON"); return; }
    try {
      await axios.put(`${API}/content/contact`, { email, whatsapp, location, socialLinks: parsed }, { headers });
      await fetchAll(); flash("Contact updated!");
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Email</label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></div>
        <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">WhatsApp Number</label>
          <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className={inputCls} placeholder="1234567890" /></div>
      </div>
      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Location</label>
        <Input value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} /></div>
      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Social Links (JSON)</label>
        <Textarea value={socialLinks} onChange={(e) => setSocialLinks(e.target.value)} rows={6} className={`font-mono text-xs ${inputCls}`} /></div>
      <Button onClick={save} className="bg-[#E63946] text-white hover:bg-[#F25C69]"><Save size={14} className="mr-2" />Save Contact</Button>
    </div>
  );
}

// ── Messages Panel ─────────────────────────────────────
function MessagesPanel({ messages, headers, fetchAll, flash }) {
  const [expanded, setExpanded] = useState(null);

  const markRead = async (id) => {
    try { await axios.put(`${API}/messages/${id}/read`, {}, { headers }); await fetchAll(); } catch (e) { console.error(e); }
  };

  const deleteMsg = async (id) => {
    try { await axios.delete(`${API}/messages/${id}`, { headers }); await fetchAll(); flash("Deleted"); } catch (e) { console.error(e); }
  };

  if (messages.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 border border-white/10 flex items-center justify-center bg-[#0F0F11] rounded-full">
          <Mail size={24} className="text-zinc-600" />
        </div>
        <p className="text-zinc-500 text-sm">No messages yet</p>
        <p className="text-zinc-700 text-xs mt-1">Contact form submissions will appear here</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-zinc-400 mb-4">{messages.length} messages</p>
      <div className="space-y-2">
        {messages.map((msg) => (
          <div key={msg.id} className={`p-4 bg-[#0F0F11] border transition-colors ${!msg.read ? "border-[#E63946]/30" : "border-white/5"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setExpanded(expanded === msg.id ? null : msg.id); if (!msg.read) markRead(msg.id); }}>
                <div className="flex items-center gap-2">
                  {!msg.read && <div className="w-2 h-2 rounded-full bg-[#E63946] shrink-0" />}
                  <p className="text-sm font-semibold text-white truncate">{msg.subject}</p>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-zinc-400">{msg.name}</span>
                  <span className="text-xs text-zinc-600">{msg.email}</span>
                  {msg.company && <span className="text-xs text-zinc-600">{msg.company}</span>}
                  <span className="text-[10px] text-zinc-700 font-mono">{new Date(msg.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <button onClick={() => deleteMsg(msg.id)} className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
            {expanded === msg.id && (
              <div className="mt-3 pt-3 border-t border-white/5">
                <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                <div className="mt-3 flex items-center gap-3">
                  <a href={`mailto:${msg.email}?subject=Re: ${msg.subject}`} className="text-xs text-[#E63946] hover:text-[#F25C69] transition-colors">
                    Reply via Email
                  </a>
                  <span className="text-[10px] text-zinc-700 font-mono">{new Date(msg.createdAt).toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Portfolio Hero Editor ──────────────────────────────
function PortfolioHeroEditor({ data, headers, fetchAll, flash }) {
  const [videoUrl, setVideoUrl] = useState(data.videoUrl || "");
  const [subtitle, setSubtitle] = useState(data.subtitle || "");
  const [stats, setStats] = useState(JSON.stringify(data.stats || [], null, 2));

  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(stats); } catch { flash("Invalid stats JSON"); return; }
    try {
      await axios.put(`${API}/content/portfolio-hero`, { videoUrl, subtitle, stats: parsed }, { headers });
      await fetchAll(); flash("Portfolio hero updated!");
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-4">
      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Background Video URL (Vimeo embed)</label>
        <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} className={inputCls} placeholder="https://player.vimeo.com/video/..." /></div>
      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Subtitle</label>
        <Textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2} className={inputCls} /></div>
      <div><label className="block text-xs text-zinc-500 mb-1 font-mono uppercase tracking-wider">Stats (JSON array of strings)</label>
        <Textarea value={stats} onChange={(e) => setStats(e.target.value)} rows={6} className={`font-mono text-xs ${inputCls}`} /></div>
      <Button onClick={save} className="bg-[#E63946] text-white hover:bg-[#F25C69]"><Save size={14} className="mr-2" />Save Hero</Button>
    </div>
  );
}

